import type { components } from '@multaqa/api-client'
import { type UseMutationOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { useRef } from 'react'
import { api, apiOrigin } from '@/lib/api'
import { downloadFile } from '@/lib/download'

/**
 * A mutation whose request body is the current company, read at CALL time
 * rather than at hook-creation time.
 *
 * TanStack Query hooks capture their closure at render; a screen whose `me`
 * query resolves after first render would otherwise send the mutation with an
 * empty X-Company-Id. The ref holds the latest companyId between renders.
 */
function useFreshCompanyId(companyId: string | null): () => string {
  const ref = useRef(companyId)
  ref.current = companyId
  return () => ref.current ?? ''
}

/** Peel the `{ data: … }` envelope a JsonResource adds. */
function unwrap<T>(wrapper: { data?: unknown }, endpoint: string): T {
  if (wrapper.data === undefined || wrapper.data === null) {
    throw new Error(`Malformed ${endpoint} response: missing data envelope.`)
  }
  return wrapper.data as T
}

export type Account = components['schemas']['AccountResource']
export type AccountImportPreview = components['schemas']['AccountImportPreviewResource']
export type AccountImportResult = components['schemas']['AccountImportResultResource']
export type AccountImportRow = components['schemas']['AccountImportCommitRequest']['rows'][number]

/** The five account types a chart carries; each has a `chart.types.*` label. */
export type ChartAccountType = 'asset' | 'liability' | 'equity' | 'revenue' | 'expense'

/**
 * The chart of accounts' filters, owned by the route's URL search params
 * (`chart.tsx` validates them). Applied on the client — `GET /accounting/
 * accounts` takes no query parameters — but their state never leaves the URL.
 */
export interface ChartFilters {
  q?: string
  type?: ChartAccountType
  status?: 'active' | 'inactive'
}

export interface AccountStoreBody {
  code: string
  name: Record<string, string>
  type: string
  parent_id?: string | null
  is_postable?: boolean
  currency?: string | null
  cash_flow_category?: string | null
}

/**
 * Create an account in the company's chart (FR-ACC-001..006).
 *
 * The chart screen listed 36 accounts and offered no way to add one, though
 * `POST /accounting/accounts` has existed since Wave 1 — the MVP walkthrough
 * calls the chart "the company's own — editable", which it was not.
 */
export function useCreateAccount(
  companyId: string | null,
  options?: Omit<UseMutationOptions<Account, Error, AccountStoreBody>, 'mutationFn'>,
) {
  const queryClient = useQueryClient()
  const currentCompany = useFreshCompanyId(companyId)

  return useMutation<Account, Error, AccountStoreBody>({
    ...options,
    mutationFn: async (body) => {
      const response = await api.POST('/accounting/accounts', {
        headers: { 'X-Company-Id': currentCompany() },
        // Schema artifact: Scramble infers `name: string[]` from `['required','array']`
        // while the API accepts `{ en, ar }` (LocalizedText). Narrow cast documents it.
        body: body as unknown as components['schemas']['AccountStoreRequest'],
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to create the account')
      return unwrap<Account>(response.data ?? {}, 'accounts')
    },
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({
        queryKey: ['companies', companyId, 'accounting', 'accounts'],
      })
      options?.onSuccess?.(...args)
    },
  })
}

/**
 * Rename an account.
 *
 * `PATCH /accounting/accounts/{account}` has existed since Wave 1 with no
 * caller, so a typo in an account name — or a missing Arabic name — was
 * permanent once the account was created.
 *
 * The endpoint validates with `AccountStoreRequest`, where `code` and `type`
 * are REQUIRED, so the whole record is resent even when only the name moved.
 * The screen still offers no way to change a code or a type: every posting
 * already refers to them, and renumbering a live chart is a migration story,
 * not a dialog.
 */
export function useUpdateAccount(
  companyId: string | null,
  options?: Omit<
    UseMutationOptions<Account, Error, { accountId: string; body: AccountStoreBody }>,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient()
  const currentCompany = useFreshCompanyId(companyId)

  return useMutation<Account, Error, { accountId: string; body: AccountStoreBody }>({
    ...options,
    mutationFn: async ({ accountId, body }) => {
      const response = await api.PATCH('/accounting/accounts/{account}', {
        headers: { 'X-Company-Id': currentCompany() },
        params: { path: { account: accountId } },
        // Schema artifact: see POST /accounting/accounts above.
        body: body as unknown as components['schemas']['AccountStoreRequest'],
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to update the account')
      return unwrap<Account>(response.data ?? {}, 'accounts')
    },
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({
        queryKey: ['companies', companyId, 'accounting', 'accounts'],
      })
      options?.onSuccess?.(...args)
    },
  })
}

/**
 * Deactivate an account. Postings make an account permanent, so it is
 * deactivated and never deleted — the same rule the confirm dialog states.
 */
export function useDeactivateAccount(
  companyId: string | null,
  options?: Omit<UseMutationOptions<void, Error, string>, 'mutationFn'>,
) {
  const queryClient = useQueryClient()
  const currentCompany = useFreshCompanyId(companyId)

  return useMutation<void, Error, string>({
    ...options,
    mutationFn: async (accountId) => {
      const response = await api.POST('/accounting/accounts/{account}/deactivate', {
        headers: { 'X-Company-Id': currentCompany() },
        params: { path: { account: accountId } },
      })
      if (response.error) {
        throw new Error(response.error.message ?? 'Failed to deactivate the account')
      }
    },
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({
        queryKey: ['companies', companyId, 'accounting', 'accounts'],
      })
      options?.onSuccess?.(...args)
    },
  })
}

/**
 * R-10 (plans/57): the way back. The exact mirror of `useDeactivateAccount` —
 * the backend authorises both with the same `accounting_account.delete` key,
 * so the frontend does not split them either. The response body is an
 * unwrapped-once AccountResource that nothing needs: invalidating the
 * accounts prefix is what flips the row's status badge.
 */
export function useActivateAccount(
  companyId: string | null,
  options?: Omit<UseMutationOptions<void, Error, string>, 'mutationFn'>,
) {
  const queryClient = useQueryClient()
  const currentCompany = useFreshCompanyId(companyId)

  return useMutation<void, Error, string>({
    ...options,
    mutationFn: async (accountId) => {
      const response = await api.POST('/accounting/accounts/{account}/activate', {
        headers: { 'X-Company-Id': currentCompany() },
        params: { path: { account: accountId } },
      })
      if (response.error) {
        throw new Error(response.error.message ?? 'Failed to activate the account')
      }
    },
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({
        queryKey: ['companies', companyId, 'accounting', 'accounts'],
      })
      options?.onSuccess?.(...args)
    },
  })
}

/**
 * Lock or unlock an account: a locked account stays in the chart and its
 * reports, but the ledger refuses new postings to it. One hook for both
 * directions; the API gates both with `accounting_account.edit`.
 */
export function useChangeAccountLock(
  companyId: string | null,
  options?: Omit<UseMutationOptions<void, Error, { id: string; lock: boolean }>, 'mutationFn'>,
) {
  const queryClient = useQueryClient()
  const currentCompany = useFreshCompanyId(companyId)

  return useMutation<void, Error, { id: string; lock: boolean }>({
    ...options,
    mutationFn: async ({ id, lock }) => {
      const init = {
        headers: { 'X-Company-Id': currentCompany() },
        params: { path: { account: id } },
      }
      const response = lock
        ? await api.POST('/accounting/accounts/{account}/lock', init)
        : await api.POST('/accounting/accounts/{account}/unlock', init)
      if (response.error)
        throw new Error(response.error.message ?? 'Failed to change the account lock')
    },
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({
        queryKey: ['companies', companyId, 'accounting', 'accounts'],
      })
      options?.onSuccess?.(...args)
    },
  })
}

export function usePreviewAccountImport(
  companyId: string | null,
  options?: Omit<UseMutationOptions<AccountImportPreview, Error, File>, 'mutationFn'>,
) {
  const currentCompany = useFreshCompanyId(companyId)

  return useMutation<AccountImportPreview, Error, File>({
    ...options,
    mutationFn: async (file) => {
      const form = new FormData()
      form.set('file', file)
      const response = await api.POST('/accounting/accounts/import/preview', {
        headers: { 'X-Company-Id': currentCompany() },
        body: form as unknown as components['schemas']['AccountImportPreviewRequest'],
      })
      if (response.error)
        throw new Error(response.error.message ?? 'Failed to preview account import')
      return unwrap<AccountImportPreview>(response.data ?? {}, 'account-import-preview')
    },
  })
}

export function useCommitAccountImport(
  companyId: string | null,
  options?: Omit<UseMutationOptions<AccountImportResult, Error, AccountImportRow[]>, 'mutationFn'>,
) {
  const queryClient = useQueryClient()
  const currentCompany = useFreshCompanyId(companyId)

  return useMutation<AccountImportResult, Error, AccountImportRow[]>({
    ...options,
    mutationFn: async (rows) => {
      const response = await api.POST('/accounting/accounts/import/commit', {
        headers: { 'X-Company-Id': currentCompany() },
        body: { rows },
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to import accounts')
      return unwrap<AccountImportResult>(response.data ?? {}, 'account-import-commit')
    },
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({
        queryKey: ['companies', companyId, 'accounting', 'accounts'],
      })
      options?.onSuccess?.(...args)
    },
  })
}

export function exportAccounts(
  companyId: string,
  format: 'csv' | 'xlsx',
  fallbackName: string,
): Promise<void> {
  const path = `/api/accounting/accounts/export?format=${format}`
  const url = apiOrigin === '' ? path : `${apiOrigin}${path}`

  return downloadFile(url, fallbackName, { 'X-Company-Id': companyId })
}
